import { useState } from "react";
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
} from "@datapay-by-datacom/design-system";
import { useAuthenticated } from "../../authn";
import {
  AccessMode,
  CapabilityKind,
  type Capability,
  type CapabilityCreate,
} from "../../generated/core-api";
import { useUserOrgs } from "../../org/hooks";
import {
  useCapabilities,
  useCreateCapability,
  useDeleteCapability,
  useUpdateCapability,
} from "../hooks";
import {
  CAPABILITY_BINDING_OPTIONS,
  defaultMarkdownRetrieveConfig,
  defaultVectorKbConfig,
  formatMarkdownRetrieveConfigSummary,
  formatVectorKbConfigSummary,
  getMarkdownRetrieveConfigValidationError,
  getVectorKbConfigValidationError,
  MARKDOWN_RETRIEVE_BINDING,
  MARKDOWN_RETRIEVE_OUTPUT_SCHEMA,
  MAX_ALTERNATE_QUERIES,
  MAX_CANDIDATE_K,
  MAX_KNOWLEDGE_SOURCES,
  parseMarkdownRetrieveConfig,
  parseVectorKbConfig,
  RETRIEVAL_VECTOR_KB_BINDING,
  serializeMarkdownRetrieveConfig,
  serializeVectorKbConfig,
  setDefaultTopK,
  setMaxTopK,
  VECTOR_KB_OUTPUT_SCHEMA,
  type MarkdownRetrieveConfig,
  type VectorKbConfig,
} from "../types";
import {
  buildKnowledgeSourceOptions,
  formatAccessLabel,
  getCapabilityBindingLabel,
  getKnowledgeSourceSummary,
  type CatalogOrgRef,
} from "../utils/catalogDisplay";
import {
  buildMarkdownRetrieveInputSchema,
  DEFAULT_MARKDOWN_RETRIEVE_DOC_ID_DESCRIPTION,
  isValidMarkdownRetrieveInputSchema,
  MARKDOWN_RETRIEVE_DOC_ID_PROPERTY,
  parseMarkdownRetrieveDocIdDescription,
} from "../utils/markdownRetrieveInputSchema";
import {
  buildVectorKbInputSchema,
  defaultVectorKbToolParameters,
  isValidVectorKbInputSchema,
  listVectorKbToolParameterNames,
  parseVectorKbToolParameters,
  type VectorKbToolParameters,
} from "../utils/vectorKbInputSchema";
import {
  formatCapabilityName,
  isValidCapabilityName,
} from "../utils/capabilityName";
import {
  describeFusionPipelineFromForm,
  FUSION_EXPANSION_VARIANT_COUNT,
} from "../utils/fusionPipeline";
import { CreateAccordion } from "./CreateAccordion";
import { FusionPipelinePreview } from "./FusionPipelinePreview";
import { MarkdownRetrieveFields } from "./MarkdownRetrieveFields";
import { OrgSourcesEditor } from "./OrgSourcesEditor";
import styles from "./AgentCatalog.module.css";

function buildVectorKbCreatePayload(
  name: string,
  owner: string,
  access: AccessMode,
  config: VectorKbConfig,
  toolParameters: VectorKbToolParameters,
): CapabilityCreate {
  return {
    name,
    kind: CapabilityKind.tool,
    binding: RETRIEVAL_VECTOR_KB_BINDING,
    owner,
    access,
    input_schema: buildVectorKbInputSchema(toolParameters),
    output_schema: VECTOR_KB_OUTPUT_SCHEMA,
    config: serializeVectorKbConfig(config),
  };
}

function buildMarkdownRetrieveCreatePayload(
  name: string,
  owner: string,
  access: AccessMode,
  config: MarkdownRetrieveConfig,
  docIdDescription: string,
): CapabilityCreate {
  return {
    name,
    kind: CapabilityKind.tool,
    binding: MARKDOWN_RETRIEVE_BINDING,
    owner,
    access,
    input_schema: buildMarkdownRetrieveInputSchema(docIdDescription),
    output_schema: MARKDOWN_RETRIEVE_OUTPUT_SCHEMA,
    config: serializeMarkdownRetrieveConfig(config),
  };
}

function ToolParametersEditor({
  idPrefix,
  parameters,
  onChange,
}: {
  idPrefix: string;
  parameters: VectorKbToolParameters;
  onChange: (parameters: VectorKbToolParameters) => void;
}) {
  return (
    <div className={styles.parameterSection}>
      <h4 className={styles.parameterSectionTitle}>Tool parameters</h4>
      <p className={styles.parameterHint}>
        These become the arguments on the retrieve tool. Prefer a full
        natural-language question. Alternate queries are distinct search
        angles (statute cites, clause numbers), not paraphrases. Omit optional
        parameters so the agent only sees question.
      </p>
      <div className={styles.fieldRow}>
        <label htmlFor={`${idPrefix}-question-description`}>
          Question description
        </label>
        <textarea
          id={`${idPrefix}-question-description`}
          className={`${styles.textArea} ${styles.parameterTextArea}`}
          value={parameters.questionDescription}
          onChange={(e) =>
            onChange({ ...parameters, questionDescription: e.target.value })
          }
        />
      </div>
      <label
        className={styles.checkboxRow}
        htmlFor={`${idPrefix}-include-alternate-queries`}
      >
        <input
          id={`${idPrefix}-include-alternate-queries`}
          type="checkbox"
          checked={parameters.includeAlternateQueries}
          onChange={(e) =>
            onChange({
              ...parameters,
              includeAlternateQueries: e.target.checked,
            })
          }
        />
        Include alternate_queries so the agent can supply distinct search angles
      </label>
      {parameters.includeAlternateQueries && (
        <div className={styles.fieldRow}>
          <label htmlFor={`${idPrefix}-alternate-queries-description`}>
            alternate_queries description
          </label>
          <textarea
            id={`${idPrefix}-alternate-queries-description`}
            className={`${styles.textArea} ${styles.parameterTextArea}`}
            value={parameters.alternateQueriesDescription}
            onChange={(e) =>
              onChange({
                ...parameters,
                alternateQueriesDescription: e.target.value,
              })
            }
          />
        </div>
      )}
      <label className={styles.checkboxRow} htmlFor={`${idPrefix}-include-top-k`}>
        <input
          id={`${idPrefix}-include-top-k`}
          type="checkbox"
          checked={parameters.includeTopK}
          onChange={(e) =>
            onChange({ ...parameters, includeTopK: e.target.checked })
          }
        />
        Include top_k so the agent can override the default
      </label>
      {parameters.includeTopK && (
        <div className={styles.fieldRow}>
          <label htmlFor={`${idPrefix}-top-k-description`}>
            top_k description
          </label>
          <textarea
            id={`${idPrefix}-top-k-description`}
            className={`${styles.textArea} ${styles.parameterTextArea}`}
            value={parameters.topKDescription}
            onChange={(e) =>
              onChange({ ...parameters, topKDescription: e.target.value })
            }
          />
        </div>
      )}
    </div>
  );
}

function CapabilityIdentityFields({
  idPrefix,
  name,
  onNameChange,
  access,
  onAccessChange,
  binding,
  onBindingChange,
  allowWrite = true,
}: {
  idPrefix: string;
  name: string;
  onNameChange: (name: string) => void;
  access: AccessMode;
  onAccessChange: (access: AccessMode) => void;
  binding?: string;
  onBindingChange?: (binding: string) => void;
  allowWrite?: boolean;
}) {
  return (
    <>
      <div className={styles.fieldRow}>
        <label htmlFor={`${idPrefix}-name`}>Name</label>
        <input
          id={`${idPrefix}-name`}
          className={styles.textInput}
          value={name}
          spellCheck={false}
          autoComplete="off"
          placeholder="Recipes-Strict-Retrieval"
          onChange={(e) => onNameChange(formatCapabilityName(e.target.value))}
        />
        <p className={styles.fieldHint}>
          Letters, digits, hyphens, and underscores only. Spaces become hyphens.
        </p>
      </div>
      {onBindingChange && (
        <div className={styles.fieldRow}>
          <label htmlFor={`${idPrefix}-binding`}>Tool binding</label>
          <select
            id={`${idPrefix}-binding`}
            className={styles.select}
            value={binding}
            onChange={(e) => onBindingChange(e.target.value)}
          >
            {CAPABILITY_BINDING_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={!option.enabled}
              >
                {option.label}
                {!option.enabled ? " (coming soon)" : ""}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className={styles.fieldRow}>
        <label htmlFor={`${idPrefix}-access`}>Access</label>
        <select
          id={`${idPrefix}-access`}
          className={styles.select}
          value={access}
          onChange={(e) => onAccessChange(e.target.value as AccessMode)}
        >
          <option value={AccessMode.read}>Read</option>
          {allowWrite && <option value={AccessMode.write}>Write</option>}
        </select>
        {!allowWrite && (
          <p className={styles.fieldHint}>
            This binding only reads documents, so write access does not apply.
          </p>
        )}
      </div>
    </>
  );
}

function VectorKbFields({
  idPrefix,
  vectorConfig,
  onVectorConfigChange,
  toolParameters,
  onToolParametersChange,
  orgs,
}: {
  idPrefix: string;
  vectorConfig: VectorKbConfig;
  onVectorConfigChange: (config: VectorKbConfig) => void;
  toolParameters: VectorKbToolParameters;
  onToolParametersChange: (parameters: VectorKbToolParameters) => void;
  orgs: CatalogOrgRef[];
}) {
  const configValidationError = getVectorKbConfigValidationError(vectorConfig);
  const pipelineModel = describeFusionPipelineFromForm(
    vectorConfig,
    toolParameters,
  );

  return (
    <>
      <OrgSourcesEditor
        idPrefix={idPrefix}
        label="Knowledge sources"
        sources={vectorConfig.knowledge_sources}
        options={buildKnowledgeSourceOptions(orgs)}
        maxSources={MAX_KNOWLEDGE_SOURCES}
        addLabel="Add knowledge source"
        hint={`Up to ${MAX_KNOWLEDGE_SOURCES} indexes. Fan-out is query × org.`}
        onChange={(sources) =>
          onVectorConfigChange({ ...vectorConfig, knowledge_sources: sources })
        }
      />
      <ToolParametersEditor
        idPrefix={idPrefix}
        parameters={toolParameters}
        onChange={onToolParametersChange}
      />
      <div className={styles.fusionLayout}>
        <div className={styles.fusionFields}>
          <div className={styles.parameterSection}>
            <h4 className={styles.parameterSectionTitle}>Fusion retrieval</h4>
            <p className={styles.parameterHint}>
              How this tool searches and ranks. The agent cannot change these
              settings when it calls retrieve.
            </p>
          </div>
        <div className={styles.fieldRow}>
          <label htmlFor={`${idPrefix}-default-top-k`}>Default top_k</label>
          <input
            id={`${idPrefix}-default-top-k`}
            type="number"
            min={1}
            max={vectorConfig.max_top_k}
            className={styles.textInput}
            value={vectorConfig.default_top_k}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                return;
              }
              const parsed = Number(raw);
              if (!Number.isFinite(parsed)) {
                return;
              }
              onVectorConfigChange(setDefaultTopK(vectorConfig, parsed));
            }}
          />
          <p className={styles.fieldHint}>
            How many chunks the agent receives when it does not set top_k.
            Cannot exceed max top_k.
          </p>
        </div>
        <div className={styles.fieldRow}>
          <label htmlFor={`${idPrefix}-max-top-k`}>Max top_k</label>
          <input
            id={`${idPrefix}-max-top-k`}
            type="number"
            min={vectorConfig.default_top_k}
            className={styles.textInput}
            value={vectorConfig.max_top_k}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                return;
              }
              const parsed = Number(raw);
              if (!Number.isFinite(parsed)) {
                return;
              }
              onVectorConfigChange(setMaxTopK(vectorConfig, parsed));
            }}
          />
          <p className={styles.fieldHint}>
            Upper limit if the agent does set top_k. Cannot be below default
            top_k.
          </p>
          {configValidationError?.includes("Max top_k") && (
            <p className={styles.errorText} role="alert">
              {configValidationError}
            </p>
          )}
        </div>
        <div className={styles.fieldRow}>
          <label htmlFor={`${idPrefix}-candidate-k`}>Candidate k</label>
          <input
            id={`${idPrefix}-candidate-k`}
            type="number"
            min={1}
            max={MAX_CANDIDATE_K}
            className={styles.textInput}
            value={vectorConfig.candidate_k}
            onChange={(e) =>
              onVectorConfigChange({
                ...vectorConfig,
                candidate_k: Number(e.target.value),
              })
            }
          />
          <p className={styles.fieldHint}>
            First-pass pool from each knowledge base, for each search string
            (the question and any alternates). Raise this to cast a wider net
            before ranking; the agent still only sees up to top_k chunks.
            Range 1–{MAX_CANDIDATE_K}.
          </p>
        </div>
        <div className={styles.fieldRow}>
          <label htmlFor={`${idPrefix}-rerank-pool`}>
            Rerank pool (optional)
          </label>
          <input
            id={`${idPrefix}-rerank-pool`}
            type="number"
            min={1}
            className={styles.textInput}
            value={vectorConfig.rerank_pool ?? ""}
            onChange={(e) =>
              onVectorConfigChange({
                ...vectorConfig,
                rerank_pool:
                  e.target.value === "" ? null : Number(e.target.value),
              })
            }
          />
          <p className={styles.fieldHint}>
            Maximum fused unique chunks sent to Cohere rerank. Leave blank to
            rerank every fused chunk (no shortlist). When set, must be at least
            max top_k.
          </p>
          {configValidationError?.includes("Rerank pool") && (
            <p className={styles.errorText} role="alert">
              {configValidationError}
            </p>
          )}
        </div>
        <div className={styles.fieldRow}>
          <label htmlFor={`${idPrefix}-max-alternate-queries`}>
            Max alternate queries
          </label>
          <input
            id={`${idPrefix}-max-alternate-queries`}
            type="number"
            min={0}
            max={MAX_ALTERNATE_QUERIES}
            className={styles.textInput}
            value={vectorConfig.max_alternate_queries}
            onChange={(e) =>
              onVectorConfigChange({
                ...vectorConfig,
                max_alternate_queries: Number(e.target.value),
              })
            }
          />
          <p className={styles.fieldHint}>
            The agent can send up to this many extra searches
            (0–{MAX_ALTERNATE_QUERIES}) when alternate_queries is on the tool.
            Expansion generates at most {FUSION_EXPANSION_VARIANT_COUNT} and is
            also clipped by this cap.
          </p>
        </div>
        <label
          className={styles.checkboxRow}
          htmlFor={`${idPrefix}-expand-when-empty`}
        >
          <input
            id={`${idPrefix}-expand-when-empty`}
            type="checkbox"
            checked={vectorConfig.expand_when_empty}
            onChange={(e) =>
              onVectorConfigChange({
                ...vectorConfig,
                expand_when_empty: e.target.checked,
              })
            }
          />
          Expand when empty (generate extra searches if the agent omits alternate_queries)
        </label>
        <div className={styles.fieldRow}>
          <label htmlFor={`${idPrefix}-min-score`}>
            Min score (optional)
          </label>
          <input
            id={`${idPrefix}-min-score`}
            type="number"
            min={0}
            max={1}
            step={0.01}
            className={styles.textInput}
            value={vectorConfig.min_score ?? ""}
            onChange={(e) =>
              onVectorConfigChange({
                ...vectorConfig,
                min_score:
                  e.target.value === "" ? null : Number(e.target.value),
              })
            }
          />
          <p className={styles.fieldHint}>
            After ranking, drop chunks scored below this (0–1). Leave blank to
            keep every ranked result.
          </p>
        </div>
        </div>
        <FusionPipelinePreview model={pipelineModel} />
      </div>
    </>
  );
}

function CapabilityCard({
  capability,
  orgs,
}: {
  capability: Capability;
  orgs: CatalogOrgRef[];
}) {
  const update = useUpdateCapability(capability.id);
  const deleteMutation = useDeleteCapability();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(formatCapabilityName(capability.name));
  const [access, setAccess] = useState<AccessMode>(
    capability.access ?? AccessMode.read,
  );
  const isMarkdownRetrieve = capability.binding === MARKDOWN_RETRIEVE_BINDING;
  const config = parseVectorKbConfig(capability.config);
  const markdownStoredConfig = parseMarkdownRetrieveConfig(capability.config);
  const [vectorConfig, setVectorConfig] = useState<VectorKbConfig>(config);
  const [markdownConfig, setMarkdownConfig] = useState<MarkdownRetrieveConfig>(
    markdownStoredConfig,
  );
  const [toolParameters, setToolParameters] = useState(
    parseVectorKbToolParameters(capability.input_schema),
  );
  // Read-only in the form: the stored wording round-trips through a save so an
  // older capability keeps its description instead of being rewritten.
  const docIdDescription = parseMarkdownRetrieveDocIdDescription(
    capability.input_schema,
  );

  const configValidationError = isMarkdownRetrieve
    ? getMarkdownRetrieveConfigValidationError(markdownConfig)
    : getVectorKbConfigValidationError(vectorConfig);

  const handleSave = () => {
    if (configValidationError) {
      return;
    }
    update.mutate(
      isMarkdownRetrieve
        ? {
            name,
            access,
            config: serializeMarkdownRetrieveConfig(markdownConfig),
            input_schema: buildMarkdownRetrieveInputSchema(docIdDescription),
          }
        : {
            name,
            access,
            config: serializeVectorKbConfig(vectorConfig),
            input_schema: buildVectorKbInputSchema(toolParameters),
          },
      { onSuccess: () => setIsEditing(false) },
    );
  };

  const sources = isMarkdownRetrieve
    ? markdownStoredConfig.org_sources
    : config.knowledge_sources;
  const displayedParameters = parseVectorKbToolParameters(
    capability.input_schema,
  );
  const schemaIsValid = isMarkdownRetrieve
    ? isValidMarkdownRetrieveInputSchema(capability.input_schema)
    : isValidVectorKbInputSchema(capability.input_schema);

  return (
    <div className={`${styles.cardCell} ${isEditing ? styles.cardCellEditing : ""}`}>
      <Card>
        <CardHeader>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>{capability.name}</h3>
          </div>
        </CardHeader>
        <CardBody>
          {isEditing ? (
            <>
              <CapabilityIdentityFields
                idPrefix={`edit-${capability.id}`}
                name={name}
                onNameChange={setName}
                access={access}
                onAccessChange={setAccess}
                allowWrite={!isMarkdownRetrieve}
              />
              {isMarkdownRetrieve ? (
                <MarkdownRetrieveFields
                  idPrefix={`edit-${capability.id}`}
                  config={markdownConfig}
                  onConfigChange={setMarkdownConfig}
                  docIdDescription={docIdDescription}
                  orgs={orgs}
                />
              ) : (
                <VectorKbFields
                  idPrefix={`edit-${capability.id}`}
                  vectorConfig={vectorConfig}
                  onVectorConfigChange={setVectorConfig}
                  toolParameters={toolParameters}
                  onToolParametersChange={setToolParameters}
                  orgs={orgs}
                />
              )}
            </>
          ) : (
            <>
              <div className={styles.metaRow}>
                <span className={styles.badge}>
                  {getCapabilityBindingLabel(capability.binding)}
                </span>
                <span className={styles.badge}>
                  {formatAccessLabel(capability.access)}
                </span>
                {schemaIsValid &&
                  (isMarkdownRetrieve
                    ? [MARKDOWN_RETRIEVE_DOC_ID_PROPERTY]
                    : listVectorKbToolParameterNames(displayedParameters)
                  ).map((parameter) => (
                    <span key={parameter} className={styles.badge}>
                      {parameter}
                    </span>
                  ))}
              </div>
              {schemaIsValid && (
                <p className={styles.cardDescription}>
                  {isMarkdownRetrieve
                    ? parseMarkdownRetrieveDocIdDescription(
                        capability.input_schema,
                      )
                    : displayedParameters.questionDescription}
                </p>
              )}
              {!schemaIsValid && (
                <p className={styles.errorText}>
                  {isMarkdownRetrieve
                    ? "Tool input_schema does not match the markdown.retrieve contract. Edit and save to restore a schema with a single required doc_id string."
                    : "Tool input_schema does not match the retrieval.vector_kb contract. Edit and save to restore a valid question / optional alternate_queries and top_k schema."}
                </p>
              )}
              <p className={styles.sourceSummary}>
                {getKnowledgeSourceSummary(sources, orgs)}
              </p>
              <p className={styles.sourceSummary}>
                {isMarkdownRetrieve
                  ? formatMarkdownRetrieveConfigSummary(markdownStoredConfig)
                  : formatVectorKbConfigSummary(config)}
              </p>
            </>
          )}
        </CardBody>
        <CardFooter>
          {isEditing ? (
            <ButtonGroup>
              <Button
                onPress={handleSave}
                isDisabled={
                  update.isPending ||
                  !isValidCapabilityName(name) ||
                  configValidationError != null
                }
              >
                {update.isPending ? "Saving…" : "Save"}
              </Button>
              <Button variant="secondary" onPress={() => setIsEditing(false)}>
                Cancel
              </Button>
            </ButtonGroup>
          ) : (
            <div className={styles.actions}>
              <Button
                variant="secondary"
                size="small"
                onPress={() => setIsEditing(true)}
              >
                Edit
              </Button>
              <Button
                variant="secondary"
                size="small"
                onPress={() => deleteMutation.mutate(capability.id)}
                isDisabled={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}

export function CapabilityCatalogSection() {
  const { userId } = useAuthenticated();
  const orgsQuery = useUserOrgs(userId);
  const orgs: CatalogOrgRef[] = orgsQuery.data ?? [];

  const capabilitiesQuery = useCapabilities();
  const createMutation = useCreateCapability();
  const [createOpen, setCreateOpen] = useState(false);

  const [name, setName] = useState("");
  const [binding, setBinding] = useState(RETRIEVAL_VECTOR_KB_BINDING);
  const [access, setAccess] = useState<AccessMode>(AccessMode.read);
  const [vectorConfig, setVectorConfig] = useState<VectorKbConfig>(
    defaultVectorKbConfig("", "internal"),
  );
  const [toolParameters, setToolParameters] = useState(
    defaultVectorKbToolParameters(),
  );
  const [markdownConfig, setMarkdownConfig] = useState(
    defaultMarkdownRetrieveConfig(),
  );
  const docIdDescription = DEFAULT_MARKDOWN_RETRIEVE_DOC_ID_DESCRIPTION;

  const isMarkdownRetrieve = binding === MARKDOWN_RETRIEVE_BINDING;
  const configValidationError = isMarkdownRetrieve
    ? getMarkdownRetrieveConfigValidationError(markdownConfig)
    : getVectorKbConfigValidationError(vectorConfig);

  const handleBindingChange = (nextBinding: string) => {
    setBinding(nextBinding);
    // Markdown retrieval is read-only, so a Write selection carried over from
    // another binding would be unsubmittable and the option is gone.
    if (nextBinding === MARKDOWN_RETRIEVE_BINDING) {
      setAccess(AccessMode.read);
    }
  };

  const handleCreate = () => {
    if (configValidationError) {
      return;
    }
    let payload: CapabilityCreate;
    if (isMarkdownRetrieve) {
      payload = buildMarkdownRetrieveCreatePayload(
        name,
        userId,
        access,
        markdownConfig,
        docIdDescription,
      );
    } else if (binding === RETRIEVAL_VECTOR_KB_BINDING) {
      payload = buildVectorKbCreatePayload(
        name,
        userId,
        access,
        vectorConfig,
        toolParameters,
      );
    } else {
      return;
    }
    createMutation.mutate(payload, {
      onSuccess: () => {
        setName("");
        setVectorConfig(defaultVectorKbConfig("", "internal"));
        setToolParameters(defaultVectorKbToolParameters());
        setMarkdownConfig(defaultMarkdownRetrieveConfig());
        setCreateOpen(false);
      },
    });
  };

  const capabilities = capabilitiesQuery.data ?? [];

  return (
    <section className={styles.section}>
      <p className={styles.sectionDescription}>
        Platform-level fused hybrid retrieve tools. Service accounts grant
        snapshots of these capabilities at the organisation level.
      </p>

      <CreateAccordion
        title="New capability"
        isOpen={createOpen}
        onToggle={() => setCreateOpen((open) => !open)}
      >
        <CapabilityIdentityFields
          idPrefix="create-capability"
          name={name}
          onNameChange={setName}
          access={access}
          onAccessChange={setAccess}
          binding={binding}
          onBindingChange={handleBindingChange}
          allowWrite={!isMarkdownRetrieve}
        />
        {isMarkdownRetrieve ? (
          <MarkdownRetrieveFields
            idPrefix="create-capability"
            config={markdownConfig}
            onConfigChange={setMarkdownConfig}
            docIdDescription={docIdDescription}
            orgs={orgs}
          />
        ) : (
          <VectorKbFields
            idPrefix="create-capability"
            vectorConfig={vectorConfig}
            onVectorConfigChange={setVectorConfig}
            toolParameters={toolParameters}
            onToolParametersChange={setToolParameters}
            orgs={orgs}
          />
        )}
        <ButtonGroup>
          <Button
            onPress={handleCreate}
            isDisabled={
              createMutation.isPending ||
              !isValidCapabilityName(name) ||
              !userId.trim() ||
              (!isMarkdownRetrieve &&
                vectorConfig.knowledge_sources.length === 0) ||
              configValidationError != null
            }
          >
            {createMutation.isPending ? "Creating…" : "Create capability"}
          </Button>
        </ButtonGroup>
        {createMutation.isError && (
          <p className={styles.errorText}>Failed to create capability.</p>
        )}
      </CreateAccordion>

      {capabilitiesQuery.isLoading && <p>Loading capabilities…</p>}
      {capabilitiesQuery.isError && (
        <p className={styles.errorText}>Failed to load capabilities.</p>
      )}
      {capabilitiesQuery.data && capabilities.length === 0 && (
        <p className={styles.emptyState}>
          No capabilities yet. Open New capability to add a tool definition.
        </p>
      )}
      {capabilities.length > 0 && (
        <div className={styles.cardGrid}>
          {capabilities.map((capability) => (
            <CapabilityCard key={capability.id} capability={capability} orgs={orgs} />
          ))}
        </div>
      )}
    </section>
  );
}
